import { useCallback, useEffect, useState } from 'react';
import type { ListSharesResponse, ShareSummary } from '@markbird/shared';
import { useStore } from '../store/useStore';
import { getIdToken } from '../lib/auth';
import { t } from '../i18n';
import { Icon } from './Icon';
import { Modal } from './Modal';
import { formatDate, formatDateTime } from '../lib/format';
import { deleteLocalShare, listLocalShares, type LocalShare } from '../lib/localShares';

/** Expiry presets for editing an existing share (mirrors ShareOptionsDialog). */
const EXPIRY_PRESETS: { key: string; ms: number | null }[] = [
  { key: 'shareOpt.never', ms: null },
  { key: 'shareOpt.1h', ms: 60 * 60 * 1000 },
  { key: 'shareOpt.1d', ms: 24 * 60 * 60 * 1000 },
  { key: 'shareOpt.7d', ms: 7 * 24 * 60 * 60 * 1000 },
  { key: 'shareOpt.30d', ms: 30 * 24 * 60 * 60 * 1000 },
];

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; shares: ShareSummary[] };

/**
 * Lists the signed-in user's shares with copy + revoke. Owner-scoped: the API
 * only returns shares created by the caller's uid, authenticated with a fresh
 * Firebase ID token. Revoking hits `DELETE /api/shares/:token`, after which the
 * link 404s for everyone.
 */
export function ShareManagerDialog() {
  useStore((s) => s.locale); // re-render on language change
  const open = useStore((s) => s.sharesOpen);
  const setOpen = useStore((s) => s.setSharesOpen);
  const user = useStore((s) => s.user);
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [revoking, setRevoking] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [savingExpiry, setSavingExpiry] = useState<string | null>(null);
  // Links made on this device, with the secret that deletes them. Shown to
  // everyone: without an account they are the only list there is.
  const [local, setLocal] = useState<LocalShare[]>([]);

  const load = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const token = await getIdToken();
      if (!token) {
        setState({ status: 'error', message: t('shareMgr.signin') });
        return;
      }
      const res = await fetch('/api/shares', {
        headers: { authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(t('shareMgr.loadFail'));
      const data = (await res.json()) as ListSharesResponse;
      setState({ status: 'ready', shares: data.shares });
    } catch (err) {
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : t('shareMgr.loadFail'),
      });
    }
  }, []);

  useEffect(() => {
    if (open && user) void load();
    if (open) void listLocalShares().then(setLocal);
  }, [open, user, load]);

  if (!open) return null;

  const revoke = async (shareToken: string) => {
    setRevoking(shareToken);
    try {
      const token = await getIdToken();
      if (!token) return;
      const res = await fetch(`/api/shares/${shareToken}`, {
        method: 'DELETE',
        headers: { authorization: `Bearer ${token}` },
      });
      if (res.ok && state.status === 'ready') {
        setState({
          status: 'ready',
          shares: state.shares.filter((s) => s.token !== shareToken),
        });
      }
    } finally {
      setRevoking(null);
    }
  };

  const updateExpiry = async (shareToken: string, ms: number | null) => {
    setSavingExpiry(shareToken);
    try {
      const token = await getIdToken();
      if (!token) return;
      const expiresAt = ms == null ? null : Date.now() + ms;
      const res = await fetch(`/api/shares/${shareToken}`, {
        method: 'PATCH',
        headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
        body: JSON.stringify({ expiresAt }),
      });
      if (res.ok && state.status === 'ready') {
        setState({
          status: 'ready',
          shares: state.shares.map((s) => (s.token === shareToken ? { ...s, expiresAt } : s)),
        });
      }
    } finally {
      setSavingExpiry(null);
    }
  };

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(url);
      setTimeout(() => setCopied((c) => (c === url ? null : c)), 1500);
    } catch {
      /* clipboard may be blocked */
    }
  };

  const close = () => setOpen(false);

  return (
    <Modal onClose={close} title={t('shareMgr.title')} className="mb-modal-wide">
      <p className="mb-modal-note mb-share-explainer">{t('shareMgr.explainer')}</p>

      {user && state.status === 'loading' && <p className="mb-modal-note">{t('sp.loading')}</p>}
      {user && state.status === 'error' && <p className="mb-modal-note">{state.message}</p>}
      {user && state.status === 'ready' && state.shares.length === 0 && (
        <p className="mb-modal-note">{t('shareMgr.empty')}</p>
      )}

      {state.status === 'ready' && state.shares.length > 0 && (
        <ul className="mb-share-list">
          {state.shares.map((s) => (
            <li key={s.token} className="mb-share-row">
              <div className="mb-share-row-main">
                <span className="mb-share-row-title">{s.title || t('common.untitled')}</span>
                <span
                  className={`mb-share-badge ${s.permission === 'edit' ? 'mb-share-badge-edit' : ''}`}
                >
                  {s.permission === 'edit' ? t('shareMgr.badgeEdit') : t('shareMgr.badgeRead')}
                </span>
                {s.protected && (
                  <span
                    className="mb-share-lock"
                    title={t('shareMgr.protected')}
                    aria-label={t('shareMgr.protected')}
                  >
                    <Icon name="lock" size={13} />
                  </span>
                )}
                {s.expiresAt != null && (
                  <span className="mb-share-expiry" title={formatDateTime(s.expiresAt)}>
                    {t('shareMgr.expires', { date: formatDate(s.expiresAt) })}
                  </span>
                )}
              </div>
              <div className="mb-share-row-actions">
                <select
                  className="mb-share-expiry-select"
                  aria-label={t('shareMgr.setExpiry')}
                  disabled={savingExpiry === s.token}
                  value=""
                  onChange={(e) => {
                    const idx = Number(e.target.value);
                    if (!Number.isNaN(idx))
                      void updateExpiry(s.token, EXPIRY_PRESETS[idx]?.ms ?? null);
                  }}
                >
                  <option value="">{t('shareMgr.setExpiry')}</option>
                  {EXPIRY_PRESETS.map((o, i) => (
                    <option key={o.key} value={i}>
                      {t(o.key)}
                    </option>
                  ))}
                </select>
                <button className="mb-btn" onClick={() => void copy(s.url)}>
                  {copied === s.url ? t('common.copied') : t('shareMgr.copyLink')}
                </button>
                <button
                  className="mb-btn"
                  disabled={revoking === s.token}
                  onClick={() => void revoke(s.token)}
                >
                  {revoking === s.token ? t('shareMgr.revoking') : t('shareMgr.revoke')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <h3 className="mb-vault-section">{t('shareMgr.localTitle')}</h3>
      <p className="mb-modal-note">{t('shareMgr.localExplainer')}</p>
      {local.length === 0 ? (
        <p className="mb-modal-note">{t('shareMgr.localEmpty')}</p>
      ) : (
        <ul className="mb-share-list">
          {local.map((s) => (
            <li key={s.token} className="mb-share-row">
              <div className="mb-share-row-main">
                <span className="mb-share-row-title">{s.title || t('common.untitled')}</span>
                {/* A row that takes a whole site down should say so before it
                    is pressed, not after. */}
                {s.kind === 'site' && (
                  <span className="mb-share-badge">{t('shareMgr.isSite')}</span>
                )}
                {s.expiresAt != null && (
                  <span className="mb-share-expiry" title={formatDateTime(s.expiresAt)}>
                    {t('shareMgr.expires', { date: formatDate(s.expiresAt) })}
                  </span>
                )}
              </div>
              <div className="mb-share-row-actions">
                <button className="mb-btn" onClick={() => void copy(s.url)}>
                  {copied === s.url ? t('common.copied') : t('shareMgr.copyLink')}
                </button>
                <button className="mb-btn" onClick={() => void copy(s.deleteUrl)}>
                  {copied === s.deleteUrl ? t('common.copied') : t('shareMgr.copyDeleteLink')}
                </button>
                <button
                  className="mb-btn"
                  disabled={revoking === s.token}
                  onClick={() =>
                    void (async () => {
                      setRevoking(s.token);
                      try {
                        await deleteLocalShare(s);
                        setLocal(await listLocalShares());
                        if (state.status === 'ready') {
                          setState({
                            status: 'ready',
                            shares: state.shares.filter((x) => x.token !== s.token),
                          });
                        }
                      } finally {
                        setRevoking(null);
                      }
                    })()
                  }
                >
                  {revoking === s.token ? t('shareMgr.revoking') : t('shareMgr.revoke')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mb-modal-actions">
        <button className="mb-btn mb-btn-primary" onClick={close}>
          {t('common.done')}
        </button>
      </div>
    </Modal>
  );
}
